import type React from "react";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import {
	findMatchingSortOption,
	handleColumnSortClick,
	handleSavingSortingStateToLocalStorage,
	type SortingState,
	sortColumnsEqual,
} from "../../../../utils/multiColumnSorting";
import type { DropdownItem } from "../../Inputs/SystemDropDown";
import type { SystemSortingProps } from "./SystemSortingButtonWithPopup";

export const useSystemSortingOptionsContentState = <T,>({
	sortingOptions,
	localStorageTableKey,
	initialState,
	setSortState,
	defaultSortState,
	minEntries,
}: SystemSortingProps<T>) => {
	/**
	 * Should hold a local state and update the parent state with deferred value because this way our component is more responsive
	 */
	const [localStoredState, setLocalStoredState] = useState<SortingState<T>[]>(initialState);
	const deferredLocalStoreState = useDeferredValue(localStoredState);
	const deferredLocalStoreStateRef = useRef(deferredLocalStoreState);
	const {
		userInfo: { id: userId },
	} = useAuth();

	const sortingDropdownOptions = useMemo(
		() =>
			sortingOptions
				.map<DropdownItem<Array<keyof T>> | undefined>((option) =>
					option.sortColumns
						? {
								label: option.label,
								value: option.sortColumns,
							}
						: undefined,
				)
				.filter((option) => option !== undefined)
				.filter((option) => !localStoredState.some((state) => option && sortColumnsEqual(state.sortColumns, option.value))),
		[localStoredState, sortingOptions],
	);

	useEffect(() => {
		/**
		 * We updated our local state - we should update the parent state as well
		 */
		setSortState(deferredLocalStoreState);
		handleSavingSortingStateToLocalStorage(deferredLocalStoreState, localStorageTableKey, userId);
		deferredLocalStoreStateRef.current = deferredLocalStoreState;
	}, [deferredLocalStoreState, setSortState, localStorageTableKey, userId]);

	useEffect(() => {
		/**
		 * Parent updates manually the initial state - we should update our local state as well
		 */
		if (!initialState.every((state) => deferredLocalStoreStateRef.current.includes(state))) {
			setLocalStoredState(initialState);
		}
	}, [initialState]);

	/**
	 * An entry with no row in `sortingOptions` — an inner-use tie-breaker (e.g. the `id` column) that
	 * the popup never lists and the user therefore cannot see, reorder or clear.
	 */
	const isHiddenEntry = useCallback((item: SortingState<T>) => !findMatchingSortOption(item.sortColumns, sortingOptions), [sortingOptions]);

	/**
	 * Hidden tie-breakers belong LAST. `multiSortComparator` reads the array in priority order, so a
	 * hidden `id` sitting ahead of a visible key decides every tie before that key is consulted — the
	 * key the popup displays would then do nothing, with no row on screen to explain why.
	 */
	const withHiddenEntriesLast = useCallback(
		(entries: SortingState<T>[]) => [...entries.filter((entry) => !isHiddenEntry(entry)), ...entries.filter(isHiddenEntry)],
		[isHiddenEntry],
	);

	const handleReorder = useCallback(
		(next: SortingState<T>[]) => {
			// Drag-reorder only ever hands back the visible rows, so the hidden ones are re-appended here.
			setLocalStoredState([...next, ...initialState.filter(isHiddenEntry)]);
		},
		[isHiddenEntry, initialState],
	);

	const handleSortLabelClick = useCallback(
		(label?: string, columns?: Array<keyof T>) => {
			// Look up the option for these columns so we can carry through its customSortFunction.
			const matched = columns ? findMatchingSortOption(columns, sortingOptions) : undefined;
			return handleColumnSortClick({
				sortState: localStoredState,
				// Normalised on the way in: `append` puts the new key at the very end, which is BEHIND any
				// hidden tie-breaker already in the state.
				setSortState: (update) => setLocalStoredState((prev) => withHiddenEntriesLast(typeof update === "function" ? update(prev) : update)),
				columns,
				label,
				customSortFunction: matched?.customSortFunction,
				localStorageDetails: localStorageTableKey
					? {
							localStorageTableKey: localStorageTableKey,
							userId,
						}
					: undefined,
				allowRemovalWhenDescendingToggle: false,
				// `append`, never the default `stack`: this popup is the multi-key builder, and its
				// dropdown says "Add Another Sort". Under `stack` a pick was inserted as the new PRIMARY
				// key, so adding B to an existing sort on A silently produced [B, A] — the opposite of
				// what the control offers. Appending puts it where the list shows it, last, and toggling
				// a row that is already in the sort flips its direction where it stands.
				clickBehavior: "append",
			});
		},
		[localStoredState, localStorageTableKey, userId, sortingOptions, withHiddenEntriesLast],
	);

	const isItemClearable = useCallback(
		(item: SortingState<T>) => {
			// At the `minEntries` floor nothing more may be cleared, so the last row(s) lose their clear
			// button entirely rather than letting the popup emit a state the caller cannot accept.
			if (minEntries !== undefined && localStoredState.length <= minEntries) {
				return false;
			}
			// item is clearable only if the sortingOptions has at least one option that matches the item's sortColumns
			return findMatchingSortOption(item.sortColumns, sortingOptions) !== undefined;
		},
		[sortingOptions, minEntries, localStoredState.length],
	);

	const handleClearClicked = useCallback((e: React.MouseEvent, item: SortingState<T>) => {
		e.stopPropagation();
		setLocalStoredState((prev) => {
			return prev.filter((s) => s !== item);
		});
	}, []);

	const handleRestoreDefaultClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			setLocalStoredState(Array.from(defaultSortState ?? initialState));
		},
		[defaultSortState, initialState],
	);
	const handleOptionToAddSelected = useCallback(
		(selectedValue: (keyof T)[] | undefined) => {
			handleSortLabelClick(selectedValue ? findMatchingSortOption(selectedValue, sortingOptions)?.label : undefined, selectedValue);
		},
		[handleSortLabelClick, sortingOptions],
	);

	const filteredLocalState = localStoredState.filter(
		(item) =>
			/**
			 * We should not display items that are not in the sortingOptions because they are for inner use only (e.g. like ID column sorting)
			 */
			findMatchingSortOption(item.sortColumns, sortingOptions) !== undefined,
	);

	return {
		sortingDropdownOptions,
		handleReorder,
		handleSortLabelClick,
		isItemClearable,
		handleClearClicked,
		handleRestoreDefaultClick,
		handleOptionToAddSelected,
		filteredLocalState,
	};
};
